import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight, RefreshCw, Loader2, Clock, User,
  Timer, Play, Pause, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';
import { useTranslation } from '../../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WORK QUEUE - AI-Powered Prioritization
 * 
 * Design principle: Answer ONE question - "What should I work on NOW?"
 */
const CaseQueueViewMinimal = () => {
  const navigate = useNavigate();
  const { language } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [queueData, setQueueData] = useState<any>(null);
  const [skippedCases, setSkippedCases] = useState<Set<string>>(new Set());
  const [activeTimers, setActiveTimers] = useState<Record<string, boolean>>({});
  
  const fetchQueue = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/case-queue`, {
        params: { language },
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueueData(response.data);
      setSkippedCases(new Set()); // Reset skipped on refresh
    } catch (error) {
      console.error('Error fetching case queue:', error);
      toast.error(language === 'en' ? 'Could not load work queue' : 'Kunne ikke laste arbeidskøen');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [language]);
  
  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);
  
  const openCase = (caseItem: any) => {
    // Mark as viewed
    const token = auth.getToken();
    axios.post(`${API}/case-queue/${caseItem.id}/mark-viewed`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
    
    navigate(`/lexy/ai-workspace/${caseItem.id}`);
  };
  
  const skipCase = (caseId: string) => {
    setSkippedCases(prev => new Set([...prev, caseId]));
  };
  
  const toggleTimer = (caseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveTimers(prev => ({ ...prev, [caseId]: !prev[caseId] }));
    toast.success(activeTimers[caseId] 
      ? (language === 'en' ? 'Timer stopped' : 'Timer stoppet')
      : (language === 'en' ? 'Timer started' : 'Timer startet')
    );
  };

  // Get the top recommended case (excluding skipped ones)
  const getTopCase = () => {
    const cases = queueData?.cases || [];
    for (const c of cases) {
      if (!skippedCases.has(c.id) && c.queue_category !== 'blocked') {
        return c;
      }
    }
    return null;
  };
  
  // Get remaining cases
  const getRemainingCases = () => {
    const cases = queueData?.cases || [];
    const topCase = getTopCase();
    return cases.filter((c: any) => c.id !== topCase?.id && !skippedCases.has(c.id));
  };

  const topCase = getTopCase();
  const remainingCases = getRemainingCases();
  const today = new Date().toLocaleDateString(language === 'en' ? 'en-GB' : 'nb-NO', { 
    day: 'numeric', 
    month: 'long' 
  });

  // Translations
  const t = {
    title: language === 'en' ? 'Work Queue' : 'Arbeidskø',
    activeCases: (n: number) => language === 'en' ? `${n} active cases` : `${n} aktive saker`,
    topCaseLabel: language === 'en' ? 'Your most important case now' : 'Din viktigste sak nå',
    nextStep: language === 'en' ? 'Next step' : 'Neste steg',
    estimatedTime: language === 'en' ? 'Estimated time' : 'Estimert tid',
    openCase: language === 'en' ? 'Open case' : 'Åpne sak',
    showNext: language === 'en' ? 'Show next recommendation' : 'Vis neste anbefaling',
    restOfQueue: (n: number) => language === 'en' ? `Rest of queue (${n} cases)` : `Resten av køen (${n} saker)`,
    noCases: language === 'en' ? 'No cases in queue' : 'Ingen saker i køen',
    allDone: language === 'en' ? 'All cases are handled. Great job!' : 'Alle saker er håndtert. Godt jobbet!',
    skippedAll: language === 'en' ? 'You\'ve reviewed all recommendations' : 'Du har gått gjennom alle anbefalinger',
    refresh: language === 'en' ? 'Refresh to start over' : 'Oppdater for å starte på nytt'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-8 py-12">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-semibold text-gray-900">{t.title}</h1>
          <button
            onClick={() => fetchQueue(true)}
            disabled={refreshing}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className="text-sm text-gray-400 mb-12">{today} · {t.activeCases(queueData?.stats?.total || 0)}</p>

        {/* Top Case - The Main Recommendation */}
        {topCase ? (
          <motion.div
            key={topCase.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-16"
          >
            <p className="text-sm text-gray-500 mb-4">{t.topCaseLabel}</p>
            
            <div className="border-l-2 border-gray-900 pl-6">
              {/* Case title and metadata */}
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {topCase.topic || topCase.title}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {[topCase.case_type, topCase.client_name].filter(Boolean).join(' · ')}
              </p>
              
              {/* Reasons - Why this case */}
              <div className="bg-gray-50 rounded-xl p-5 mb-6">
                <ul className="space-y-2.5">
                  {(topCase.action_reasons || []).slice(0, 4).map((reason: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                      <span className="text-gray-400 mt-0.5">•</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Next step */}
              <div className="mb-6">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1.5">{t.nextStep}</p>
                <p className="text-gray-900">{topCase.next_step}</p>
              </div>
              
              {/* Estimated time */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
                <Clock className="w-4 h-4" />
                <span>{t.estimatedTime}: {topCase.estimated_time}</span>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => openCase(topCase)}
                  className="px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  {t.openCase}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => skipCase(topCase.id)}
                  className="px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm transition-colors"
                >
                  {t.showNext}
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          /* No cases or all skipped */
          <div className="text-center py-16 mb-12">
            {queueData?.stats?.total === 0 ? (
              <>
                <p className="text-lg text-gray-900 mb-2">{t.noCases}</p>
                <p className="text-sm text-gray-500">{t.allDone}</p>
              </>
            ) : (
              <>
                <p className="text-lg text-gray-900 mb-2">{t.skippedAll}</p>
                <button
                  onClick={() => setSkippedCases(new Set())}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  {t.refresh}
                </button>
              </>
            )}
          </div>
        )}

        {/* Rest of Queue */}
        {remainingCases.length > 0 && (
          <div>
            <p className="text-sm text-gray-400 mb-4">{t.restOfQueue(remainingCases.length)}</p>
            
            <div className="space-y-1">
              {remainingCases.map((c: any) => (
                <CaseRow
                  key={c.id}
                  caseItem={c}
                  onOpen={() => openCase(c)}
                  language={language}
                  isTimerActive={activeTimers[c.id]}
                  onToggleTimer={(e: React.MouseEvent) => toggleTimer(c.id, e)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple Case Row
const CaseRow = ({ caseItem, onOpen, language, isTimerActive, onToggleTimer }: any) => {
  const getDeadlineText = () => {
    const days = caseItem.deadline_days;
    if (days === null || days === undefined) return null;
    if (days < 0) return { text: language === 'en' ? 'Overdue' : 'Forsinket', urgent: true };
    if (days === 0) return { text: language === 'en' ? 'Today' : 'I dag', urgent: true };
    if (days === 1) return { text: language === 'en' ? 'Tomorrow' : 'I morgen', urgent: true };
    if (days <= 7) return { text: `${days}d`, urgent: false };
    return { text: `${days}d`, urgent: false };
  };
  
  const deadline = getDeadlineText();

  return (
    <div
      onClick={onOpen}
      className="group flex items-center gap-4 py-3 px-4 -mx-4 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
    >
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-gray-700">
          {caseItem.topic || caseItem.title}
        </h3>
        {caseItem.client_name && (
          <p className="text-xs text-gray-400 truncate">{caseItem.client_name}</p>
        )}
      </div>
      
      <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onToggleTimer}
          className={`p-1.5 rounded-md transition-colors ${
            isTimerActive ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          {isTimerActive ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </button>
      </div>
      
      {deadline && (
        <span className={`text-xs font-medium ${deadline.urgent ? 'text-red-600' : 'text-gray-400'}`}>
          {deadline.text}
        </span>
      )}
      
      <span className="text-xs text-gray-400">{caseItem.estimated_time}</span>
      
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
    </div>
  );
};

export default CaseQueueViewMinimal;
